import React, { useCallback, useEffect, useState } from "react";
import { FiAlertTriangle, FiRefreshCw } from "react-icons/fi";
import HrmCommandPalette from "./HrmCommandPalette";
import HrmHeader from "./HrmHeaderRevamped";
import HrmSidebar from "./HrmSidebar";
import AssetsScreen from "../screens/AssetsScreen";
import BenefitsScreen from "../screens/BenefitsScreen";
import DashboardScreen from "../screens/DashboardScreen";
import DirectoryScreen from "../screens/DirectoryScreen";
import EventsScreen from "../screens/EventsScreen";
import ExpensesScreen from "../screens/ExpensesScreen";
import LearningScreen from "../screens/LearningScreen";
import LeaveScreen, { getAttendanceMonthRows, isActionableUnknownAttendanceRow } from "../screens/LeaveScreen";
import MyFormsScreen from "../screens/MyFormsScreen";
import PayrollScreen from "../screens/PayrollScreen";
import PerformanceScreen from "../screens/PerformanceScreen";
import PoliciesScreen from "../screens/PoliciesScreen";
import ProfileScreen from "../screens/ProfileScreen";
import ProjectsScreen from "../screens/ProjectsScreen";
import RequestsScreen from "../screens/RequestsScreen";
import TimeEntriesScreen from "../screens/TimeEntriesScreen";
import ComponentLoader from "./ComponentLoader";

const toDateKey = (value) => {
  if (!value) {
    return "";
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  const raw = String(value).trim();
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  const date = new Date(raw);
  if (!Number.isNaN(date.getTime())) {
    return date.toISOString().slice(0, 10);
  }

  return "";
};

const getCurrentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const navigationLabels = {
  assets: "Assets",
  benefits: "Benefits",
  dashboard: "Dashboard",
  directory: "Directory",
  events: "Events",
  expenses: "Expenses",
  leave: "Leave & Attendance",
  learning: "Training",
  myCases: "My Cases",
  myForms: "My Forms",
  payroll: "Payroll",
  performance: "Performance",
  policies: "Policies",
  profile: "My Profile",
  projects: "Projects",
  requests: "Requests",
  time: "Time Entries",
};

const formatUnknownAttendanceDate = (row = {}) => {
  if (row.date instanceof Date && !Number.isNaN(row.date.getTime())) {
    return row.date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  }
  const rawDate = row.dateKey || row.rawLoggedDate || row.loggedDate || row.date;
  const date = rawDate ? new Date(String(rawDate).includes("T") ? rawDate : `${rawDate}T00:00:00`) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })
    : "--";
};

const normalizeComparableText = (value) => String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
const normalizeComparableId = (value) => normalizeComparableText(value).replace(/[^a-z0-9]/g, "");

const isOpenCriticalTask = (entity = {}) => {
  const priority = normalizeComparableText(entity.priority || entity.Priority__c);
  if (priority !== "critical") {
    return false;
  }

  const status = normalizeComparableText(entity.status || entity.Status__c).replace(/[\s_-]/g, "");
  return !["closed", "done", "completed", "complete", "cancelled", "canceled", "archived", "inactive"].includes(status);
};

const isTaskAssignedToUser = (entity = {}, employee = {}) => {
  const userIds = [employee.id, employee.contactId, employee.employeeSalesforceId, employee.employeeId]
    .map(normalizeComparableId)
    .filter(Boolean);
  const entityIds = [entity.assignedToId, entity.assignedToContactId, entity.assigneeId, entity.Assigned_To__c]
    .map(normalizeComparableId)
    .filter(Boolean);
  const userEmail = normalizeComparableText(employee.email);
  const entityEmail = normalizeComparableText(entity.assignedToEmail || entity.assigneeEmail);
  const userName = normalizeComparableText(employee.name);
  const entityName = normalizeComparableText(entity.assignedTo || entity.assigneeName);

  if (userIds.length && entityIds.some((id) => userIds.includes(id))) {
    return true;
  }
  if (userEmail && entityEmail && userEmail === entityEmail) {
    return true;
  }
  return Boolean(userName && entityName && userName === entityName);
};

export const getPendingCriticalTasks = (projectEntities = [], employee = {}) =>
  (Array.isArray(projectEntities) ? projectEntities : []).filter((entity) => isOpenCriticalTask(entity) && isTaskAssignedToUser(entity, employee));

const getAttendanceLogsWithActiveToday = (attendance = {}) => {
  const source = attendance || {};
  const logs = Array.isArray(source.logs) ? source.logs : [];
  const todayKey = toDateKey(new Date());
  const attendanceDateKey = toDateKey(source.rawLoggedDate || source.loggedDate || source.date);
  const isPresentToday =
    attendanceDateKey === todayKey &&
    Boolean(source.status === "checked-in" || source.checkInAt || source.checkInTime);
  const hasCurrentAttendanceLog = logs.some((log) => {
    const logDateKey = toDateKey(log.rawLoggedDate || log.loggedDate || log.date || log.dateKey);
    return logDateKey && logDateKey === todayKey;
  });

  if (!isPresentToday || hasCurrentAttendanceLog) {
    return logs;
  }

  return [
    {
      id: "active-attendance-log",
      rawLoggedDate: source.rawLoggedDate || new Date(),
      loggedDate: source.loggedDate,
      status: "Present",
    },
    ...logs,
  ];
};

const DashboardLayout = ({
  activeNav,
  adminUsersLoading,
  assetAllocations,
  attendance,
  attendanceMarkingAllowed = false,
  attendanceNetworkMessage = "",
  attendanceNetworkResolved = false,
  dataError,
  dataReady = false,
  dataLoading,
  profileSaveBlocked = false,
  defaulterLeaderboard,
  filterLoading,
  expenses,
  directoryPeople,
  filteredPolicies,
  forms,
  formsActionRequired = false,
  holidays,
  isAdmin,
  canCreateProject,
  canViewEvents,
  canViewMyCases,
  leaveBalances,
  leaveCredits,
  leaveRequests,
  managedUsers,
  monthTimesheet,
  onAction,
  onAssetsLoaded,
  onAttendanceToggle,
  onCreateProject,
  onCreateProjectEntity,
  onDownloadPayslip,
  onCloseEvent,
  onLogout,
  onManagedUserChange,
  onNavigate,
  onOpenEvent,
  onProjectAccountSearch,
  onProjectEntityAttachFiles,
  onProjectEntityChecklistAdd,
  onProjectEntityChecklistUpdate,
  onProjectEntityComment,
  onProjectEntityFileDownload,
  onProjectEntityFiltersChange,
  onProjectEntityLogTime,
  onProjectEntityStatusChange,
  onProjectEntityUpdate,
  onFormsChange,
  onPolicyOpen,
  onPayslipSelect,
  onPoliciesChange,
  onProfileDocumentDelete,
  onProfileDocumentUpload,
  onProfileSave,
  onReloadData,
  onCloseRequest,
  onOpenRequest,
  onRequestFileDownload,
  onRefreshTimesheet,
  onSubmitRequestComment,
  onRequestInlineImageUpload,
  onSubmitReferral,
  onUpdateRequest,
  onSubmitTime,
  onSubmitTimesheetApproval,
  onTimeCellEdit,
  onTimesheetCellToggle,
  onTimesheetMonthChange,
  payrollSlips,
  profile,
  projectEntityDeepLinkId,
  projectEntities,
  projectOptions,
  taskOptions = [],
  searchTerm,
  selectedManagedUserId,
  selectedTimesheetCellIds,
  selectedTimesheetCellCount,
  selectedTimesheetDate,
  selectedTimesheetMonth,
  selectedTimesheetMonthId,
  timesheetMonths,
  isTimesheetLocked,
  timesheetLockMessage,
  timeProjectOptions,
  onTimesheetDateSelect,
  operationLoading,
  totalHours,
  user,
  viewUserId,
  viewedUser,
  requests,
  workerRequests,
  requestDetailLoading,
  selectedRequest,
  selectedEventId,
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [attendanceHistoryOpenRequest, setAttendanceHistoryOpenRequest] = useState(0);
  const closeCommand = useCallback(() => setCommandOpen(false), []);
  const dataLoadingLabel =
    filterLoading === "employee"
      ? "Switching employee"
      : filterLoading === "time"
        ? "Applying month filter"
        : filterLoading === "projects"
          ? "Refreshing project entities"
          : "Refreshing HRM data";
  const attendanceLoading = operationLoading === "attendance-save";
  const timeLoading = operationLoading === "time-save" || operationLoading === "time-submit" || operationLoading === "time-bulk-fill" || operationLoading === "time-refresh";
  const timeLoadingLabel =
    operationLoading === "time-refresh"
      ? "Refreshing timesheet"
      : operationLoading === "time-submit"
        ? "Submitting timesheet"
        : operationLoading === "time-bulk-fill"
          ? "Filling timesheet"
          : "Saving timesheet";
  const payrollLoading = operationLoading === "payroll-download";
  const globalReloading = operationLoading === "global-refresh";
  const showScreenLoader = dataLoading && !["dashboard", "time", "leave", "payroll", "profile", "projects", "assets", "performance", "learning"].includes(activeNav);
  const layoutAttendanceRows = getAttendanceMonthRows(
    getAttendanceLogsWithActiveToday(attendance),
    leaveRequests,
    holidays,
    getCurrentMonthKey()
  );
  const unknownAttendanceRows = layoutAttendanceRows.filter((row) => isActionableUnknownAttendanceRow(row));
  const unknownAttendanceDateText = unknownAttendanceRows
    .slice(0, 6)
    .map(formatUnknownAttendanceDate)
    .join(", ");
  const hiddenUnknownDateCount = Math.max(unknownAttendanceRows.length - 6, 0);
  const hasUnknownAttendance = dataReady && unknownAttendanceRows.length > 0;
  const pendingCriticalTasks = getPendingCriticalTasks(projectEntities, viewedUser || user);
  const hasPendingCriticalTasks = dataReady && pendingCriticalTasks.length > 0;
  const criticalTaskTitles = pendingCriticalTasks
    .slice(0, 3)
    .map((task) => task.title || task.key)
    .filter(Boolean)
    .join(", ");
  const hiddenCriticalTaskCount = Math.max(pendingCriticalTasks.length - 3, 0);
  const openAttendanceHistory = () => {
    setAttendanceHistoryOpenRequest((current) => current + 1);
    onNavigate("leave");
  };
  const headerNotifications = [
    hasPendingCriticalTasks
      ? {
          id: "critical-tasks",
          title: `${pendingCriticalTasks.length} critical task${pendingCriticalTasks.length === 1 ? "" : "s"}`,
          detail: "Open Projects and complete the assigned work.",
          tone: "danger",
          onSelect: () => onNavigate("projects"),
        }
      : null,
    hasUnknownAttendance
      ? {
          id: "attendance",
          title: `${unknownAttendanceRows.length} attendance ${unknownAttendanceRows.length === 1 ? "day" : "days"} need review`,
          detail: "Open attendance history and resolve unknown dates.",
          tone: "warning",
          onSelect: () => openAttendanceHistory(),
        }
      : null,
    formsActionRequired
      ? {
          id: "forms",
          title: "A form needs your response",
          detail: "Open My Forms to continue or submit it.",
          tone: "brand",
          onSelect: () => onNavigate("myForms"),
        }
      : null,
  ].filter(Boolean);
  const appClassName = [
    "new-hrm-app",
    "new-hrm-app--premium",
    sidebarCollapsed ? "new-hrm-app--sidebar-collapsed" : "",
    hasPendingCriticalTasks ? "new-hrm-app--critical-task-danger" : "",
    hasUnknownAttendance ? "new-hrm-app--attendance-danger" : "",
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    const handleCommandShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen(true);
      }
    };

    document.addEventListener("keydown", handleCommandShortcut);
    return () => document.removeEventListener("keydown", handleCommandShortcut);
  }, []);

  const renderScreen = () => {
    switch (activeNav) {
      case "time":
        return (
          <TimeEntriesScreen
            holidays={holidays}
            isTimesheetLocked={isTimesheetLocked}
            dataLoading={dataLoading}
            dataLoadingLabel={dataLoadingLabel}
            monthTimesheet={monthTimesheet}
            onAction={onAction}
            onRefreshTimesheet={onRefreshTimesheet}
            onSubmitTime={onSubmitTime}
            onSubmitTimesheetApproval={onSubmitTimesheetApproval}
            onTimeCellEdit={onTimeCellEdit}
            onTimesheetCellToggle={onTimesheetCellToggle}
            onTimesheetMonthChange={onTimesheetMonthChange}
            selectedTimesheetCellIds={selectedTimesheetCellIds}
            selectedTimesheetCellCount={selectedTimesheetCellCount}
            selectedTimesheetMonth={selectedTimesheetMonth}
            selectedTimesheetMonthId={selectedTimesheetMonthId}
            projectOptions={timeProjectOptions || projectOptions}
            taskOptions={taskOptions}
            selectedTimesheetDate={selectedTimesheetDate}
            timesheetMonths={timesheetMonths}
            onTimesheetDateSelect={onTimesheetDateSelect}
            totalHours={totalHours}
            timeLoading={timeLoading}
            timeLoadingLabel={timeLoadingLabel}
            timesheetLockMessage={timesheetLockMessage}
          />
        );
      case "profile":
        return (
          <ProfileScreen
            saveBlocked={profileSaveBlocked}
            loading={dataLoading}
            loadingLabel={dataLoadingLabel}
            onDocumentDelete={onProfileDocumentDelete}
            onDocumentUpload={onProfileDocumentUpload}
            onProfileSave={onProfileSave}
            profile={profile}
          />
        );
      case "myForms":
        return (
          <MyFormsScreen
            forms={forms}
            loading={showScreenLoader}
            loadingLabel={dataLoadingLabel}
            onFormsChange={onFormsChange}
            viewUserId={viewUserId}
          />
        );
      case "events":
        return (
          <EventsScreen
            onCloseEvent={onCloseEvent}
            onOpenEvent={onOpenEvent}
            selectedEventId={selectedEventId}
          />
        );
      case "projects":
        return (
          <ProjectsScreen
            assignees={directoryPeople}
            canCreateAnyProjectEntity={canCreateProject}
            canCreateProject={canCreateProject}
            defaultAssignee={viewedUser}
            entities={projectEntities}
            loading={dataLoading || operationLoading === "project-filter"}
            loadingLabel={operationLoading === "project-filter" ? "Refreshing project entities" : dataLoadingLabel}
            onAccountSearch={onProjectAccountSearch}
            onCreateProject={onCreateProject}
            onCreateProjectEntity={onCreateProjectEntity}
            onEntityAttachFiles={onProjectEntityAttachFiles}
            onEntityChecklistAdd={onProjectEntityChecklistAdd}
            onEntityChecklistUpdate={onProjectEntityChecklistUpdate}
            onEntityComment={onProjectEntityComment}
            onEntityFileDownload={onProjectEntityFileDownload}
            onEntityFiltersChange={onProjectEntityFiltersChange}
            onEntityLogTime={onProjectEntityLogTime}
            onEntityStatusChange={onProjectEntityStatusChange}
            onEntityUpdate={onProjectEntityUpdate}
            operationLoading={operationLoading}
            focusEntityId={projectEntityDeepLinkId}
            projects={projectOptions}
            viewUserId={viewUserId}
          />
        );
      case "leave":
        return (
          <LeaveScreen
            attendance={attendance}
            attendanceMarkingAllowed={attendanceMarkingAllowed}
            attendanceNetworkMessage={attendanceNetworkMessage}
            attendanceNetworkResolved={attendanceNetworkResolved}
            attendanceOpenRequest={attendanceHistoryOpenRequest}
            attendanceLoading={attendanceLoading}
            dataLoading={dataLoading}
            dataLoadingLabel={dataLoadingLabel}
            defaulterLeaderboard={defaulterLeaderboard}
            holidays={holidays}
            leaveBalances={leaveBalances}
            leaveCredits={leaveCredits}
            leaveRequests={leaveRequests}
            onAction={onAction}
            onAttendanceToggle={onAttendanceToggle}
          />
        );
      case "payroll":
        return <PayrollScreen loading={dataLoading || payrollLoading} loadingLabel={payrollLoading ? "Preparing payslip" : dataLoadingLabel} payrollHistory={payrollSlips} taxDocuments={[...(profile?.documents || []), ...(profile?.submittedDocuments || [])]} onAction={onAction} onDownloadPayslip={onDownloadPayslip} onPayslipSelect={onPayslipSelect} />;
      case "policies":
        return (
          <PoliciesScreen
            onAction={onAction}
            onPoliciesChange={onPoliciesChange}
            onPolicyOpen={onPolicyOpen}
            policies={filteredPolicies}
            viewUserId={viewUserId}
          />
        );
      case "expenses":
        return <ExpensesScreen expenses={expenses} onAction={onAction} />;
      case "assets":
        return (
          <AssetsScreen
            assets={assetAllocations}
            loading={dataLoading}
            loadingLabel={dataLoadingLabel}
            onAction={onAction}
            onAssetsLoaded={onAssetsLoaded}
            viewUserId={viewUserId}
          />
        );
      case "benefits":
        return (
          <BenefitsScreen
            onAction={(action) => (action === "policies" ? onNavigate("policies") : onAction(action))}
            policies={filteredPolicies}
            viewedUser={viewedUser}
          />
        );
      case "directory":
        return (
          <DirectoryScreen
            people={directoryPeople}
            referralLoading={operationLoading === "referral-create"}
            searchTerm={searchTerm}
            onSubmitReferral={onSubmitReferral}
          />
        );
      case "performance":
        return <PerformanceScreen viewUserId={viewUserId} />;
      case "learning":
        return <LearningScreen onAction={onAction} viewUserId={viewUserId} />;
      case "requests":
        return (
          <RequestsScreen
            detailLoading={requestDetailLoading}
            directoryPeople={directoryPeople}
            loading={dataLoading}
            loadingLabel={dataLoadingLabel}
            onAction={onAction}
            onCloseRequest={onCloseRequest}
            onOpenRequest={onOpenRequest}
            onRequestFileDownload={onRequestFileDownload}
            onSubmitComment={onSubmitRequestComment}
            onInlineImageUpload={onRequestInlineImageUpload}
            onUpdateRequest={onUpdateRequest}
            requests={requests}
            selectedRequest={selectedRequest}
          />
        );
      case "myCases":
        return (
          <RequestsScreen
            detailLoading={requestDetailLoading}
            directoryPeople={directoryPeople}
            loading={dataLoading}
            loadingLabel={dataLoadingLabel}
            mode="assigned"
            onAction={onAction}
            onCloseRequest={onCloseRequest}
            onOpenRequest={onOpenRequest}
            onRequestFileDownload={onRequestFileDownload}
            onSubmitComment={onSubmitRequestComment}
            onInlineImageUpload={onRequestInlineImageUpload}
            onUpdateRequest={onUpdateRequest}
            requests={workerRequests}
            selectedRequest={selectedRequest}
          />
        );
      default:
        return (
          <DashboardScreen
            showAttendanceWarning={false}
            attendance={attendance}
            attendanceMarkingAllowed={attendanceMarkingAllowed}
            attendanceNetworkMessage={attendanceNetworkMessage}
            attendanceNetworkResolved={attendanceNetworkResolved}
            attendanceLoading={attendanceLoading}
            dataReady={dataReady}
            dataLoading={dataLoading}
            dataLoadingLabel={dataLoadingLabel}
            defaulterLeaderboard={defaulterLeaderboard}
            holidays={holidays}
            leaveBalances={leaveBalances}
            leaveRequests={leaveRequests}
            monthTimesheet={monthTimesheet}
            onAction={onAction}
            onAttendanceToggle={onAttendanceToggle}
            onOpenAttendanceHistory={openAttendanceHistory}
            onOpenLeaveTab={() => onNavigate("leave")}
            onOpenTimeEntries={() => onNavigate("time")}
            onRefreshTimesheet={onRefreshTimesheet}
            onSubmitTime={onSubmitTime}
            onSubmitTimesheetApproval={onSubmitTimesheetApproval}
            onTimeCellEdit={onTimeCellEdit}
            onTimesheetCellToggle={onTimesheetCellToggle}
            onTimesheetMonthChange={onTimesheetMonthChange}
            selectedTimesheetCellIds={selectedTimesheetCellIds}
            selectedTimesheetCellCount={selectedTimesheetCellCount}
            selectedTimesheetMonth={selectedTimesheetMonth}
            selectedTimesheetMonthId={selectedTimesheetMonthId}
            projectOptions={timeProjectOptions || projectOptions}
            taskOptions={taskOptions}
            selectedTimesheetDate={selectedTimesheetDate}
            timesheetMonths={timesheetMonths}
            isTimesheetLocked={isTimesheetLocked}
            timesheetLockMessage={timesheetLockMessage}
            onTimesheetDateSelect={onTimesheetDateSelect}
            requests={requests}
            totalHours={totalHours}
            timeLoading={timeLoading}
            timeLoadingLabel={timeLoadingLabel}
          />
        );
    }
  };

  return (
    <div className={appClassName}>
      <a className="new-hrm-skip-link" href="#new-hrm-main">Skip to content</a>
      <HrmSidebar
        activeItem={activeNav}
        canViewEvents={canViewEvents}
        canViewMyCases={canViewMyCases}
        collapsed={sidebarCollapsed}
        formsAvailable={Array.isArray(forms) && forms.length > 0}
        formsActionRequired={formsActionRequired}
        onAction={onAction}
        onNavigate={onNavigate}
        onOpenCommand={() => setCommandOpen(true)}
        onReloadData={onReloadData}
        onToggleCollapse={() => setSidebarCollapsed((current) => !current)}
      />
      <div className="new-hrm-workspace">
        <HrmHeader
          activeLabel={navigationLabels[activeNav] || "Employee Hub"}
          isAdmin={isAdmin}
          adminUsersLoading={adminUsersLoading}
          holidays={holidays}
          managedUsers={managedUsers}
          monthTimesheet={monthTimesheet}
          notifications={headerNotifications}
          onAction={onAction}
          onLogout={onLogout}
          onManagedUserChange={onManagedUserChange}
          onNavigate={onNavigate}
          onOpenCommand={() => setCommandOpen(true)}
          onReloadData={onReloadData}
          reloadingData={globalReloading || dataLoading || adminUsersLoading}
          selectedManagedUserId={selectedManagedUserId}
          selectedTimesheetMonth={selectedTimesheetMonth}
          selectedTimesheetMonthId={selectedTimesheetMonthId}
          user={user}
        />

        {hasPendingCriticalTasks && (
          <div className="new-hrm-critical-task-global-warning" role="alert">
            <FiAlertTriangle aria-hidden="true" />
            <div>
              <strong>Complete your critical task ASAP.</strong>
              <p>
                {pendingCriticalTasks.length} critical HRM task {pendingCriticalTasks.length === 1 ? "is" : "are"} pending.
                {criticalTaskTitles ? ` ${criticalTaskTitles}${hiddenCriticalTaskCount ? `, +${hiddenCriticalTaskCount} more` : ""}.` : ""}
                This is not optional. Finish it now before HR locks the process.
              </p>
              <button type="button" className="new-hrm-critical-task-warning-link" onClick={() => onNavigate("projects")}>
                Open critical tasks
              </button>
            </div>
          </div>
        )}

        {hasUnknownAttendance && (
          <details className="new-hrm-attendance-global-warning new-hrm-attendance-global-warning--compact">
            <summary>{unknownAttendanceRows.length} attendance {unknownAttendanceRows.length === 1 ? "day needs" : "days need"} review</summary>
            <div>
              <strong>Attendance breach. Disciplinary escalation pending.</strong>
              <p>
                {unknownAttendanceRows.length} unknown attendance {unknownAttendanceRows.length === 1 ? "day is" : "days are"} unresolved this month.
                {unknownAttendanceDateText ? ` Unknown dates: ${unknownAttendanceDateText}${hiddenUnknownDateCount ? `, +${hiddenUnknownDateCount} more` : ""}.` : " Dates are unknown in the attendance record."}
                Fix it now. Unresolved U days may be marked Absent or LOP and escalated to HR.
              </p>
              <button type="button" className="new-hrm-attendance-warning-link" onClick={openAttendanceHistory}>
                Open attendance history
              </button>
            </div>
          </details>
        )}

        <main id="new-hrm-main" tabIndex="-1" className={`new-hrm-dashboard new-hrm-dashboard--${activeNav} new-hrm-loader-host`} aria-busy={dataLoading || adminUsersLoading || Boolean(operationLoading)}>
          {dataError && (
            <div className="new-hrm-data-banner new-hrm-data-banner--error new-hrm-load-error" role="alert">
              <span>{dataError}</span>
              <button type="button" className="new-hrm-secondary-button" onClick={onReloadData} disabled={globalReloading}>
                <FiRefreshCw aria-hidden="true" /><span>Retry</span>
              </button>
            </div>
          )}
          {renderScreen()}
          <ComponentLoader active={showScreenLoader} label={dataLoadingLabel} />
        </main>
      </div>
      <HrmCommandPalette
        canViewEvents={canViewEvents}
        canViewMyCases={canViewMyCases}
        formsAvailable={Array.isArray(forms) && forms.length > 0}
        formsActionRequired={formsActionRequired}
        onAction={onAction}
        onClose={closeCommand}
        onNavigate={onNavigate}
        open={commandOpen}
      />
    </div>
  );
};

export default DashboardLayout;
